import type { Page } from '@/lib/router';
import { CONTACT_DESCRIPTION } from '@/pages/Contact/useContactMeta';
import { SITE_DESCRIPTION, SITE_TITLE } from './siteHead';

/** The document's own title and description (index.html, from siteHead.ts), shown where no page sets one. */
export const DEFAULT_TITLE = SITE_TITLE;
export const DEFAULT_DESCRIPTION = SITE_DESCRIPTION;

/** Titles the app shell sets per page (bundle `qs()` navigate map, main chunk l.15497). */
const TITLES: Partial<Record<Page, string>> = {
  home: DEFAULT_TITLE,
  'sovereign-stack': 'The Sovereign Stack — 7 Days to Debug Your Operating System',
  'ohm-gear': 'Ω ARMAMENTA — Wear The Transformation',
  'shadow-king': 'Shadow King Journal | MUTATIO618',
  scientia: '☉ SCIENTIA — The Knowledge Architecture',
  'nostram-missionem': 'Nostram Missionem — Our Mission',
  arcana: '☧ ARCANA — The Inner Sanctum',
  kybalion: 'The Kybalion — MUTATIO618 Edition',
  contact: 'Contact — MUTATIO618',
  'sign-in': 'Sign In | MUTATIO618',
  privacy: 'Privacy Policy | MUTATIO618',
  terms: 'Terms of Service | MUTATIO618',
  refunds: 'Refund Policy | MUTATIO618',
  shipping: 'Shipping Policy | MUTATIO618',
  disclaimer: 'Health Disclaimer | MUTATIO618',
};

const DESCRIPTIONS: Partial<Record<Page, string>> = {
  home: 'MUTATIO618 — Truth through transformation. Mind, body, and soul sovereignty through ancient wisdom and modern technology.',
  'sovereign-stack':
    'The Sovereign Stack — a free 36-page, 7-day protocol to debug your operating system. Begin the work.',
  'ohm-gear': 'Ω ARMAMENTA — Wear the resistance. Embody the transformation. Premium apparel from MUTATIO618.',
  scientia: 'SCIENTIA — The knowledge architecture. Curated resources for mind, body, and soul sovereignty.',
  'nostram-missionem':
    'Our mission: to guide healing of consciousness, form, and heart. Ex Ignorantia Lux, Ex Luce Mutatio.',
  // Production's ContactPage text (legal.md §1.4), so an in-app visit ends on the same description as a hard load.
  contact: CONTACT_DESCRIPTION,
  arcana: 'The Inner Sanctum. Seven Hermetic Principles, the Four Stages of Alchemy, and Sacred Symbols.',
};

export interface PageMeta {
  readonly title: string;
  readonly description: string;
}

/**
 * The shell's title and description for a page, or null for product pages (they title
 * themselves once the product has loaded). Pages that set their own meta do so in their own
 * effect, which runs after the shell's.
 */
export function pageMetaFor(page: Page): PageMeta | null {
  if (page === 'product') return null;
  return { title: TITLES[page] ?? DEFAULT_TITLE, description: DESCRIPTIONS[page] ?? DEFAULT_DESCRIPTION };
}

/**
 * The self-referencing canonical for the page currently in the address bar.
 *
 * 2026-09-18, on ☧ 0's instruction: every SPA route serves the same index.html, which carried no
 * canonical at all, so a search engine had nothing telling it which URL owns the page. The tag is
 * written per route, from the address bar, with the query and fragment dropped.
 */
function applyCanonical(): void {
  const href = window.location.origin + window.location.pathname.replace(/\/+$/, '');
  let link = document.querySelector('link[rel="canonical"]');
  if (link === null) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href === window.location.origin ? href + '/' : href);
}

/** ML-D13: applied on hard load, in-app navigation and back/forward alike. */
export function applyPageMeta(page: Page): void {
  applyCanonical();
  const meta = pageMetaFor(page);
  if (meta === null) return;
  document.title = meta.title;
  document.querySelector('meta[name="description"]')?.setAttribute('content', meta.description);
}
